import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable, catchError, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../environment/environment';
import type {
  UpsertUserProfileRequestDto,
  PatchUserProfileRequestDto,
  UserDto,
  ConfigurePreferencesRequestDto,
  PreferencesDto,
  DeleteAccountResponseDto,
  AccountDeletionRequestDto,
} from '../models';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly usersBase = environment.urls.backend.api;

  /** Cache de `GET /users/me` por sessão/token — uma única chamada até logout. */
  private meToken: string | null = null;
  private me$?: Observable<UserDto>;
  private _cachedUser: UserDto | null = null;

  constructor() {
    this.auth.logout$.subscribe(() => this.clearMeCache());
  }

  /**
   * Get current user profile (GET /users/me).
   * Chamada executada APENAS UMA VEZ por sessão — cache não é zerado em erro
   * (evita loop infinito de requisições). Cache é limpo só no logout.
   */
  getMe(): Observable<UserDto> {
    const token = this.auth.getSessionToken();
    if (!token) {
      this.clearMeCache();
      return throwError(() => new Error('Not authenticated'));
    }
    if (this.me$ && this.meToken === token) return this.me$;

    this.meToken = token;
    this.me$ = this.http.get<UserDto>(`${this.usersBase}/users/me`).pipe(
      tap((user) => { this._cachedUser = user; }),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        // NÃO zera o cache em erro — evita retentativas infinitas.
        // O guard trata o erro e redireciona. Nova requisição só no próximo login.
        return throwError(() => err);
      })
    );

    return this.me$;
  }

  /** Limpa o cache de /users/me. Chamado no logout. */
  clearMeCache(): void {
    this.me$ = undefined;
    this.meToken = null;
    this._cachedUser = null;
  }

  /** Retorna o último valor emitido por getMe(), ou null se ainda não foi carregado. */
  getCachedMe(): UserDto | null {
    return this._cachedUser;
  }

  /**
   * GET /users/me sem usar o cache de `getMe()` — valida se o token ainda é aceito
   * (polling ou revalidação após 401/403 em outro endpoint).
   */
  verifySessionActive(): Observable<UserDto> {
    const token = this.auth.getSessionToken();
    if (!token) {
      return throwError(() => new Error('Not authenticated'));
    }
    return this.http.get<UserDto>(`${this.usersBase}/users/me`);
  }

  /**
   * Create or update the authenticated user's profile (PUT /users/me/profile).
   * Used during onboarding — runs compliance verification and hard-deletes on failure.
   * Limpa o cache de getMe() após sucesso para que a próxima leitura busque dados atualizados.
   */
  upsertProfile(payload: UpsertUserProfileRequestDto): Observable<void> {
    return this.http.put<void>(`${this.usersBase}/users/me/profile`, payload).pipe(
      tap(() => this.clearMeCache())
    );
  }

  /**
   * Partial profile update (PATCH /users/me/profile).
   * Used post-onboarding for edits. Does NOT re-run compliance verification.
   * Patchable: fullName, gender, phone, countryOfBirth. At least one field required.
   */
  patchProfile(payload: PatchUserProfileRequestDto): Observable<void> {
    return this.http.patch<void>(`${this.usersBase}/users/me/profile`, payload).pipe(
      tap(() => this.clearMeCache())
    );
  }

  /**
   * Configure initial user preferences (POST /users/preferences).
   */
  configurePreferences(
    payload: ConfigurePreferencesRequestDto
  ): Observable<void> {
    return this.http.post<void>(`${this.usersBase}/users/preferences`, payload);
  }

  /**
   * Update existing user preferences (PUT /users/preferences).
   */
  updatePreferences(
    payload: ConfigurePreferencesRequestDto
  ): Observable<void> {
    return this.http.put<void>(`${this.usersBase}/users/preferences`, payload);
  }

  /**
   * Get current user preferences (GET /users/preferences).
   */
  getPreferences(): Observable<PreferencesDto> {
    return this.http.get<PreferencesDto>(`${this.usersBase}/users/preferences`);
  }

  /**
   * Initiate account deletion (DELETE /users/me/account).
   * FREE: deleted synchronously, scheduledFor absent → call logout.
   * PREMIUM: scheduledFor present → show banner, account active until that date.
   */
  deleteAccount(): Observable<DeleteAccountResponseDto> {
    return this.http.delete<DeleteAccountResponseDto>(`${this.usersBase}/users/me/account`).pipe(
      tap(() => this.clearMeCache())
    );
  }

  /**
   * Get pending account deletion request (GET /users/me/account-deletion-request).
   * Returns 404 RESOURCE_NOT_FOUND when no pending deletion.
   */
  getAccountDeletionRequest(): Observable<AccountDeletionRequestDto> {
    return this.http.get<AccountDeletionRequestDto>(`${this.usersBase}/users/me/account-deletion-request`);
  }
}
